import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import QRCode from 'qrcode';

const NatureClubDetail = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [clubDetail, setClubDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [imageError, setImageError] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const audioRef = React.useRef(null);

  useEffect(() => {
    const fetchClubDetail = async () => {
      try {
        setLoading(true);
        const response = await fetch(`https://crd-training.in/api/v1/admin/nature/${code}`);
        const result = await response.json();
        
        if (result.data) {
          setClubDetail(result.data);
        } else {
          setError('Details not found');
        }
      } catch (error) {
        console.error('Error fetching nature club detail:', error);
        setError('Failed to load details');
      } finally {
        setLoading(false);
      }
    };

    fetchClubDetail();
  }, [code]);

  // Generate QR Code
  useEffect(() => {
    const generateQRCode = async () => {
      try {
        const currentUrl = `https://crd-training.in/nature-club/${code}`;
        const qrUrl = await QRCode.toDataURL(currentUrl, {
          width: 200,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        setQrCodeUrl(qrUrl);
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    };

    if (code) {
      generateQRCode();
    }
  }, [code]);

  const downloadQRCode = () => {
    if (!qrCodeUrl) return;
    
    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `${clubDetail?.name || code}-qrcode.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadBodyContent = async () => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const element = document.getElementById('nature-club-content');
      
      if (!element) {
        alert('Content not found');
        return;
      }

      const downloadBtn = document.getElementById('download-details-btn');
      const qrDownloadBtn = document.getElementById('qr-download-btn');
      
      if (downloadBtn) downloadBtn.style.display = 'none';
      if (qrDownloadBtn) qrDownloadBtn.style.display = 'none';

      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#10b981',
        logging: false
      });

      if (downloadBtn) downloadBtn.style.display = 'flex';
      if (qrDownloadBtn) qrDownloadBtn.style.display = 'flex';

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `${clubDetail?.name || code}-details.png`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
      });

    } catch (error) {
      console.error('Error downloading content:', error);
      alert('Failed to download. Error: ' + error.message);
      
      const downloadBtn = document.getElementById('download-details-btn');
      const qrDownloadBtn = document.getElementById('qr-download-btn');
      if (downloadBtn) downloadBtn.style.display = 'flex';
      if (qrDownloadBtn) qrDownloadBtn.style.display = 'flex';
    }
  };

  const speakTamilText = async () => {
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    let textToSpeak = '';
    
    if (clubDetail.name) textToSpeak += clubDetail.name + '. ';
    if (clubDetail.common) textToSpeak += clubDetail.common + '. ';
    if (clubDetail.scientific) textToSpeak += 'Scientific name ' + clubDetail.scientific + '. ';
    if (clubDetail.family) textToSpeak += 'Family ' + clubDetail.family + '. ';
    if (clubDetail.significance) textToSpeak += 'Significance. ' + clubDetail.significance;

    if (!textToSpeak) {
      alert('No content available to read');
      return;
    }

    try {
      setIsSpeaking(true);

      const translateResponse = await fetch(
        `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=ta&dt=t&q=${encodeURIComponent(textToSpeak)}`
      );
      
      const translateData = await translateResponse.json();
      
      let translatedText = '';
      if (translateData && translateData[0]) {
        translateData[0].forEach(item => {
          if (item[0]) translatedText += item[0];
        });
      }

      if (!translatedText) {
        alert('Translation failed. Please try again.');
        setIsSpeaking(false);
        return;
      }

      const utterance = new SpeechSynthesisUtterance(translatedText);
      utterance.lang = 'ta-IN';
      utterance.rate = 0.9;
      utterance.pitch = 1;
      utterance.volume = 1;

      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = (event) => {
        console.error('Speech error:', event);
        setIsSpeaking(false);
        alert('Unable to read text. Please try again.');
      };

      const setVoiceAndSpeak = () => {
        const voices = window.speechSynthesis.getVoices();
        const tamilVoice = voices.find(voice => voice.lang.startsWith('ta'));
        if (tamilVoice) utterance.voice = tamilVoice;
        window.speechSynthesis.speak(utterance);
      };

      if (window.speechSynthesis.getVoices().length > 0) {
        setVoiceAndSpeak();
      } else {
        window.speechSynthesis.onvoiceschanged = setVoiceAndSpeak;
      }

    } catch (error) {
      console.error('Translation error:', error);
      setIsSpeaking(false);
      alert('Translation failed. Please check your internet connection.');
    }
  };

  const toggleAudioPlay = () => {
    if (!audioRef.current) return;

    if (isAudioPlaying) {
      audioRef.current.pause();
      setIsAudioPlaying(false);
    } else {
      audioRef.current.play();
      setIsAudioPlaying(true);
    }
  };

  useEffect(() => {
    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <Header />
        <main className="flex-grow flex justify-center items-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600"></div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !clubDetail) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <Header />
        <main className="flex-grow flex justify-center items-center">
          <div className="text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Details Not Found</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors shadow-md"
            >
              Back to Home
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Header />
      
      <main className="flex-grow w-full">
        <div className="bg-gradient-to-br from-green-400 via-green-500 to-green-600 min-h-screen py-8 px-4">
          <div className="max-w-7xl mx-auto" id="nature-club-content">
            
            <div className="flex items-center justify-between mb-8 bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center gap-4">
                <div className="bg-white rounded-full p-2 w-20 h-20 flex items-center justify-center">
                  <img 
                    src="/images/dept.png" 
                    alt="SIRD & PR Logo" 
                    className="w-full h-full object-contain"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900">SIRD & PR</h1>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800">TAMIL NADU</h2>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              
              <div className="flex items-start justify-start pl-8">
                <div className="bg-white rounded-3xl p-4 shadow-2xl w-full max-w-xl" style={{ height: '750px' }}>
                  {!imageError && clubDetail.image ? (
                    <img
                      src={`https://crd-training.in/uploads/nature/${clubDetail.image}`}
                      alt={clubDetail.name}
                      className="w-full h-full rounded-2xl object-cover"
                      crossOrigin="anonymous"
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <div className="w-full h-full rounded-2xl bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center">
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        className="h-32 w-32 text-green-600" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={1.5} 
                          d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" 
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-6">
                
                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 uppercase tracking-wide">
                    NAME OF THE TREE/PLANT
                  </h3>
                  <p
                    className="text-3xl md:text-4xl font-bold text-blue-600 drop-shadow-lg"
                    style={{ textShadow: '2px 2px 4px rgba(51, 56, 56, 0.3)' }}
                  >
                    {clubDetail.name}
                  </p>
                </div>

                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 uppercase tracking-wide">
                    SCIENTIFIC NAME
                  </h3>
                  <p className="text-2xl md:text-3xl font-semibold text-blue-600 italic">
                    {clubDetail.scientific}
                  </p>
                  {clubDetail.family && (
                    <p className="text-lg text-gray-600 mt-2">
                      <span className="font-semibold">Family:</span> {clubDetail.family}
                    </p>
                  )}
                </div>

                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 uppercase tracking-wide">
                    TAMIL NAME
                  </h3>
                  <p className="text-3xl md:text-4xl font-bold text-blue-600">
                    {clubDetail.name_ta}
                  </p>
                </div>

                {clubDetail.common && (
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-xl">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 uppercase tracking-wide">
                      COMMON NAME
                    </h3>
                    <p className="text-2xl md:text-3xl font-semibold text-blue-600">
                      {clubDetail.common}
                    </p>
                  </div>
                )}

                {clubDetail.significance && (
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-xl">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 uppercase tracking-wide">
                      SIGNIFICANCE :
                    </h3>
                    <p className="text-lg text-gray-800 leading-relaxed">
                      {clubDetail.significance}
                    </p>
                  </div>
                )}

                {clubDetail.reference && (
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-xl">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 uppercase tracking-wide">
                      REFERENCE :
                    </h3>
                    <a 
                      href={clubDetail.reference} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-lg text-blue-600 hover:text-blue-800 underline break-all"
                    >
                      {clubDetail.reference}
                    </a>
                  </div>
                )}

                {/* Audio Player */}
                {clubDetail.audio && (
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-xl">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-bold text-gray-900 uppercase tracking-wide">
                        AUDIO
                      </h3>
                      <button
                        onClick={toggleAudioPlay}
                        className={`${
                          isAudioPlaying ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                        } text-white px-6 py-3 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2`}
                      >
                        {isAudioPlaying ? (
                          <>
                            <svg 
                              xmlns="http://www.w3.org/2000/svg" 
                              className="h-5 w-5" 
                              fill="none" 
                              viewBox="0 0 24 24" 
                              stroke="currentColor"
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" 
                              />
                            </svg>
                            Pause
                          </>
                        ) : (
                          <>
                            <svg 
                              xmlns="http://www.w3.org/2000/svg" 
                              className="h-5 w-5" 
                              fill="none" 
                              viewBox="0 0 24 24" 
                              stroke="currentColor"
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" 
                              />
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                              />
                            </svg>
                            Play Audio
                          </>
                        )}
                      </button>
                    </div>
                    <audio
                      ref={audioRef}
                      src={`https://crd-training.in/uploads/nature/${clubDetail.audio}`}
                      onEnded={() => setIsAudioPlaying(false)}
                      onPause={() => setIsAudioPlaying(false)}
                      onPlay={() => setIsAudioPlaying(true)}
                    />
                  </div>
                )}

                {qrCodeUrl && (
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-xl">
                    <div className="flex items-center justify-between">
                      <div className="bg-white p-3 rounded-lg border-4 border-gray-900">
                        <img 
                          src={qrCodeUrl} 
                          alt="QR Code" 
                          className="w-32 h-32"
                        />
                      </div>
                      <button
                        id="qr-download-btn"
                        onClick={downloadQRCode}
                        className="bg-green-700 hover:bg-green-800 text-white px-6 py-3 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
                      >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          className="h-5 w-5" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" 
                          />
                        </svg>
                        Download QR
                      </button>
                    </div>
                  </div>
                )}

              </div>
            </div>

            <div className="mt-8 flex justify-center">
              <button
                id="download-details-btn"
                onClick={downloadBodyContent}
                className="bg-white hover:bg-gray-100 text-green-700 px-8 py-4 rounded-lg font-bold text-xl transition-all shadow-xl hover:shadow-2xl transform hover:scale-105 flex items-center gap-3"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-6 w-6" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" 
                  />
                </svg>
                Download Details
              </button>
            </div>

          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default NatureClubDetail;